import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
} from "@chakra-ui/react";
import { FieldArray, useField } from "formik";
import React, { FunctionComponent, useMemo } from "react";
import { FileItem } from "src/components/Inputs/FileItem";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { isNonEmptyString, isNullOrUndefined } from "src/services/predicates";
import { UploadFileInput, UploadFileInputProps } from "../UploadFileInput";

type Props = Omit<UploadFileInputProps, "onUpload"> & {
  name: string;
  format?: "file" | "objectUrl";
  showPreview?: boolean;
  existingFileFieldName?: string;
  description?: string;
};

export const FormikUploadFileInput: FunctionComponent<Props> = (props) => {
  const {
    name,
    showPreview = false,
    existingFileFieldName,
    multiple,
    description,
    ...uploadFileInputProps
  } = props;

  const [field, meta, helpers] = useField(name);
  const [existingField, , existingFileHelpers] = useField(
    existingFileFieldName ?? name
  );
  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Replace existing file?",
    body: "This will replace the existing file. Do you want to continue?",
  });

  const handleSingleUpload = async (file: File) => {
    if (
      !isNullOrUndefined(field.value) ||
      !isNullOrUndefined(existingField.value)
    ) {
      if (!(await confirm())) {
        return;
      }
    }

    if (existingFileFieldName) {
      existingFileHelpers.setValue(null);
    }
    helpers.setValue(file);
  };

  const handleMultiUpload = async (file: FileList) => {
    const files: File[] = Array.from(file);
    helpers.setValue([...(field.value ?? []), ...files]);
  };

  return (
    <Flex
      direction="column"
      onBlur={() => {
        helpers.setTouched(true);
      }}
      gap="2"
    >
      {confirmationDialog}
      {showPreview ? <Files {...props} /> : null}
      <FormControl isInvalid={meta.touched && !!meta.error}>
        {multiple ? (
          <UploadFileInput
            multiple={true}
            onUpload={handleMultiUpload}
            {...uploadFileInputProps}
          />
        ) : (
          <UploadFileInput
            multiple={false}
            onUpload={handleSingleUpload}
            {...uploadFileInputProps}
          />
        )}
        {meta.touched && meta.error && (
          <FormErrorMessage>{meta.error}</FormErrorMessage>
        )}
        {isNonEmptyString(description) ? (
          <FormHelperText>{description}</FormHelperText>
        ) : null}
      </FormControl>
    </Flex>
  );
};

const Files = ({ name, multiple, existingFileFieldName }: Props) => {
  const [field, , fieldHelper] = useField<File[] | File | null>(name);
  const [existingFileField, , existingFieldHelper] = useField<
    string[] | string | null
  >(existingFileFieldName ?? name);

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Remove file?",
    body: "Are you sure you want to remove this file?",
  });

  const files: File[] = useMemo(() => {
    if (field.value === null) {
      return [];
    }

    if (field.value instanceof File) {
      return [field.value];
    }

    return field.value;
  }, [field.value]);

  const existingFiles: string[] = useMemo(() => {
    if (existingFileField.value === null) {
      return [];
    }

    if (typeof existingFileField.value === "string") {
      return [existingFileField.value];
    }

    return existingFileField.value;
  }, [existingFileField.value]);

  return (
    <Flex direction="column" gap="2">
      {confirmationDialog}
      {existingFileFieldName && !isNullOrUndefined(existingFileField.value) ? (
        <FieldArray name={existingFileFieldName}>
          {(arrayHelpers) =>
            existingFiles.map((val, index) => {
              const path = URL.parse(val)?.pathname.split("/");
              return (
                <FileItem
                  key={val}
                  name={decodeURI(path?.[path?.length - 1] ?? val)}
                  previewUrl={val}
                  onDelete={async () => {
                    if (!(await confirm())) return;
                    if (multiple) {
                      arrayHelpers.remove(index);
                    } else {
                      existingFieldHelper.setValue(null);
                    }
                  }}
                />
              );
            })
          }
        </FieldArray>
      ) : null}
      {!isNullOrUndefined(field.value) ? (
        <FieldArray name={name}>
          {(arrayHelpers) =>
            files.map((file, index) => (
              <LocalFileItem
                key={`${file.name}-${file.size}-${file.lastModified}`}
                file={file}
                onDelete={async () => {
                  if (!(await confirm())) return;
                  if (multiple) {
                    arrayHelpers.remove(index);
                  } else {
                    fieldHelper.setValue(null);
                  }
                }}
              />
            ))
          }
        </FieldArray>
      ) : null}
    </Flex>
  );
};

type LocalFileItemProps = {
  file: File;
  onDelete: () => Promise<void>;
};

const LocalFileItem = ({ file, onDelete }: LocalFileItemProps) => {
  const [previewUrl, setPreviewUrl] = React.useState("");

  React.useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <FileItem name={file.name} previewUrl={previewUrl} onDelete={onDelete} />
  );
};
